
import { Component, Vue } from 'vue-property-decorator';
import AgentChatbox from '@/components/ai-chat/AgentChatbox.vue';
import { EventBus } from '@/main';

@Component({
  components: {
    AgentChatbox,
  },
})
export default class AgentChatFAB extends Vue {
  private isAgentChatOpen: boolean = false;
  private agentChatFullscreen: boolean = false;
  private showTooltip: boolean = false;
  private isCrispActive: boolean = false;
  private chatboxMode: 'overlay' | 'split' = 'overlay';
  private windowWidth: number = 0;
  private hasHandedOffToHuman: boolean = false; // Session-only flag (not persisted)

  /**
   * Check if the current route is the Atlas embed page.
   * The FAB should be hidden on this page since Atlas has its own chat interface.
   */
  private get isOnAtlasPage(): boolean {
    return this.$route?.name === 'atlasAgent';
  }

  public created() {
    // Listen for Crisp events
    EventBus.$on('crisp-opened', this.onCrispOpened);
    EventBus.$on('crisp-closed', this.onCrispClosed);

    // Listen for programmatic chatbox opening
    EventBus.$on('chatbox:open', this.openChatbox);
    EventBus.$on('chatbox:open-and-select-agent', this.openChatboxAndSelectAgent);

    // Listen for chatbox status queries (request-response pattern)
    EventBus.$on('chatbox:get-status', this.respondWithStatus);

    // Listen for handoff completion
    EventBus.$on('handoff-completed', this.onHandoffCompleted);
  }

  public mounted() {
    // Initialize window width and set initial mode
    this.windowWidth = window.innerWidth;
    this.updateChatboxMode();

    // Listen for window resize
    window.addEventListener('resize', this.handleResize);
  }

  public beforeDestroy() {
    // AS-10569: this component can be destroyed by App.vue's v-if (navigating
    // to an /assistant/* route, opening the Purchase List drawer) while the
    // chatbox is open in split mode. App.vue's `chatbox-split-active` margin
    // only clears on an explicit layout-change event — emit it here, before
    // unbinding, or the 400px content push outlives the chatbox.
    if (this.isAgentChatOpen) {
      EventBus.$emit('app:chatbox-layout-change', {
        active: false,
        mode: this.chatboxMode,
      });
    }
    EventBus.$off('crisp-opened', this.onCrispOpened);
    EventBus.$off('crisp-closed', this.onCrispClosed);
    EventBus.$off('chatbox:open', this.openChatbox);
    EventBus.$off('chatbox:open-and-select-agent', this.openChatboxAndSelectAgent);
    EventBus.$off('chatbox:get-status', this.respondWithStatus);
    EventBus.$off('handoff-completed', this.onHandoffCompleted);
    window.removeEventListener('resize', this.handleResize);
  }

  private onCrispOpened(): void {
    // Hide agent FAB and close agent chat when Crisp opens
    this.isCrispActive = true;
    this.isAgentChatOpen = false;
  }

  private onCrispClosed(): void {
    // Show agent FAB when Crisp closes
    this.isCrispActive = false;
  }

  private onHandoffCompleted(data?: {
    conversationId?: string;
    timestamp?: string;
    agentId?: string;
    agentName?: string;
  }): void {
    // Mark that handoff occurred in this session
    // Note: agentId and agentName are part of the payload but not used by this handler
    this.hasHandedOffToHuman = true;
  }

  private openCrispChat(): void {
    // Open Crisp chat when user clicks FAB after handoff
    if ((window as any).$crisp) {
      (window as any).$crisp.push(['do', 'chat:show']);
      (window as any).$crisp.push(['do', 'chat:open']);
    } else {
      // Fallback: show error message if Crisp is not loaded
      EventBus.$emit('show-snackbar', {
        message: 'Live chat is not available at the moment.',
        color: 'error',
        timeout: 5000,
      });
    }
  }

  private switchBackToAI(): void {
    // Reset handoff flag to return to AI chat mode
    this.hasHandedOffToHuman = false;

    // Open the AI chatbox immediately
    this.isAgentChatOpen = true;
    localStorage.setItem('assisty_chatbox_open', 'true');
    EventBus.$emit('chatbox:opened');
    this.emitLayoutChange();

    // Show confirmation message
    EventBus.$emit('show-snackbar', {
      message: 'Switched back to AI Assistant',
      color: 'success',
      timeout: 3000,
    });
  }

  private toggleAgentChat(): void {
    // If handoff occurred in this session, open Crisp instead of AI chat
    if (this.hasHandedOffToHuman) {
      this.openCrispChat();
      return;
    }

    this.isAgentChatOpen = !this.isAgentChatOpen;

    // Update localStorage status
    localStorage.setItem('assisty_chatbox_open', this.isAgentChatOpen ? 'true' : 'false');

    // Emit chatbox state change
    if (this.isAgentChatOpen) {
      EventBus.$emit('chatbox:opened');
    } else {
      // Note: interaction count not available when toggling via FAB button
      // Only available when chatbox emits explicit close event
      EventBus.$emit('chatbox:closed', { interactionCount: 0 });
    }

    // Emit layout change for App.vue
    this.emitLayoutChange();
  }

  private closeAgentChat(data?: { interactionCount?: number }): void {
    this.isAgentChatOpen = false;

    // Emit chatbox closed event with interaction count data
    EventBus.$emit('chatbox:closed', {
      interactionCount: data?.interactionCount || 0,
    });

    // Clear chatbox open status in localStorage
    localStorage.setItem('assisty_chatbox_open', 'false');

    // Emit layout change to restore normal layout
    EventBus.$emit('app:chatbox-layout-change', {
      active: false,
      mode: this.chatboxMode,
    });
  }

  private toggleAgentChatFullscreen(): void {
    this.agentChatFullscreen = !this.agentChatFullscreen;
  }

  private openChatbox(): void {
    // Open chatbox programmatically (e.g., from Report Builder page)
    this.isAgentChatOpen = true;

    // Set chatbox open status in localStorage
    localStorage.setItem('assisty_chatbox_open', 'true');

    EventBus.$emit('chatbox:opened');
    this.emitLayoutChange();
  }

  /**
   * Open chatbox and select a specific agent
   * @param agentId - The agent ID to select after opening
   */
  private openChatboxAndSelectAgent(agentId: string): void {
    // Open the chatbox first
    this.isAgentChatOpen = true;
    localStorage.setItem('assisty_chatbox_open', 'true');
    EventBus.$emit('chatbox:opened');
    this.emitLayoutChange();

    // Select the agent after a short delay to ensure chatbox is mounted
    setTimeout(() => {
      EventBus.$emit('chatbox:select-agent', agentId);
    }, 150);
  }

  /**
   * Respond to chatbox status queries with the actual current state
   * This enables a request-response pattern for reliable status checking
   */
  private respondWithStatus(): void {
    EventBus.$emit('chatbox:status-response', {
      isOpen: this.isAgentChatOpen,
      timestamp: Date.now(),
    });
  }

  private handleResize(): void {
    // Debounced resize handler
    this.windowWidth = window.innerWidth;
    this.updateChatboxMode();
  }

  private updateChatboxMode(): void {
    // Auto-select mode based on screen size
    const breakpoint = 1200;
    const newMode = this.windowWidth >= breakpoint ? 'split' : 'overlay';

    if (newMode !== this.chatboxMode) {
      this.chatboxMode = newMode;
      // Emit layout change if chatbox is currently open
      if (this.isAgentChatOpen) {
        this.emitLayoutChange();
      }
    }
  }

  private emitLayoutChange(): void {
    // Emit to App.vue for layout adjustment
    EventBus.$emit('app:chatbox-layout-change', {
      active: this.isAgentChatOpen,
      mode: this.chatboxMode,
    });
  }
}
